/**
 * Material UI theme configuration for the Booge IT brand.
 *
 * Defines the colour palette (primary dark-blue, secondary green),
 * typography scale, component style overrides, CSS keyframe animations,
 * and reusable style objects (glassStyle, gradientText).
 *
 * @module theme
 */

import { createTheme, alpha } from '@mui/material/styles';

/** Booge IT brand green — used for secondary actions and accents. */
const GREEN = '#43B02A';
/** Booge IT brand dark blue — used as the primary colour. */
const DARK_BLUE = '#002A3A';
/** Neutral background grey. */
const LIGHT_GREY = '#F5F5F5';

const theme = createTheme({
  palette: {
    primary: {
      main: DARK_BLUE,
      light: '#004a5e',
      dark: '#001a26',
      contrastText: '#ffffff',
    },
    secondary: {
      main: GREEN,
      light: '#6be350',
      dark: '#2d7a1c',
      contrastText: '#ffffff',
    },
    background: {
      default: LIGHT_GREY,
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '3rem',
      letterSpacing: '-0.02em',
      lineHeight: 1.15,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.25rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.3,
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
        },
        body: {
          overflowX: 'hidden',
        },
        '::selection': {
          backgroundColor: alpha(GREEN, 0.25),
          color: DARK_BLUE,
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-12px)' },
        },
        '@keyframes pulse-glow': {
          '0%, 100%': { boxShadow: `0 0 20px ${alpha(GREEN, 0.2)}` },
          '50%': { boxShadow: `0 0 40px ${alpha(GREEN, 0.4)}` },
        },
        '@keyframes shimmer': {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' },
        },
        '@keyframes gradient-shift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative' as const,
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)',
            backgroundSize: '200% 100%',
            transition: 'background-position 0.6s ease',
          },
          '&:hover::after': {
            backgroundPosition: '100% 0',
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${GREEN} 0%, #2d9c1c 100%)`,
          boxShadow: `0 4px 15px ${alpha(GREEN, 0.35)}`,
          '&:hover': {
            background: `linear-gradient(135deg, #3a9924 0%, ${GREEN} 100%)`,
            boxShadow: `0 6px 25px ${alpha(GREEN, 0.5)}`,
            transform: 'translateY(-2px)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${DARK_BLUE} 0%, #004a5e 100%)`,
          boxShadow: `0 4px 15px ${alpha(DARK_BLUE, 0.35)}`,
          '&:hover': {
            boxShadow: `0 6px 25px ${alpha(DARK_BLUE, 0.5)}`,
            transform: 'translateY(-2px)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.04)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: `0 0 0 3px ${alpha(DARK_BLUE, 0.06)}`,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(GREEN, 0.15)}`,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: '0 24px 80px rgba(0,0,0,0.15)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'all 0.3s ease',
          '&.Mui-selected': {
            fontWeight: 700,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          overflow: 'hidden',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

/** Frosted-glass effect styles for elevated surfaces. */
const glassStyle = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
};

/** Gradient text effect using the brand colour spectrum (dark-blue → green). */
const gradientText = {
  background: `linear-gradient(135deg, ${DARK_BLUE} 0%, ${GREEN} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

export default theme;
export { GREEN, DARK_BLUE, LIGHT_GREY, glassStyle, gradientText };
